import { KPI_PERIODS, REPORT_LIMITS, buildSiteTree, pctChange, type KpiPanel as Panel, type KpiPeriod, type KpiSeriesPoint } from "@fts/core";
import { useMemo } from "react";
import { HelpTip } from "../components/HelpTip";
import { Icon, type IconName } from "../components/Icon";
import { useI18n } from "../i18n";
import { formatPercent, percentParts } from "../lib/format";
import { href, navigate, type Route } from "../router/hash";
import { useSession, type TenantData } from "../state/session";
import { Loading, addDays, siteOptions, todayLocal, useLoad } from "./reports/shared";

/** Daily values as a tiny line (gaps for days without a value). Decorative; the tile states the value. */
export function Sparkline({ values, label }: { values: (number | null)[]; label: string }) {
  const nums = values.filter((v): v is number => v !== null);
  if (nums.length < 2) return <span className="spark spark-empty" aria-hidden="true" />;
  const max = Math.max(...nums);
  const min = Math.min(0, ...nums);
  const w = 96;
  const h = 28;
  const x = (i: number) => (values.length === 1 ? 0 : (i * w) / (values.length - 1));
  const y = (v: number) => h - 2 - ((v - min) / (max - min || 1)) * (h - 4);
  const parts: string[] = [];
  let pen = false;
  values.forEach((v, i) => {
    if (v === null) {
      pen = false;
      return;
    }
    parts.push(`${pen ? "L" : "M"}${x(i).toFixed(1)},${y(v).toFixed(1)}`);
    pen = true;
  });
  return (
    <svg className="spark" viewBox={`0 0 ${w} ${h}`} width={w} height={h} role="img" aria-label={label}>
      <path d={parts.join(" ")} fill="none" stroke="currentColor" strokeWidth="1.6" strokeLinejoin="round" strokeLinecap="round" />
    </svg>
  );
}

type Tone = "good" | "bad" | "neutral";

/**
 * Change against the previous period: arrow + percentage, coloured by meaning (more impacts is bad,
 * more utilisation is good); the arrow shows direction, the colour shows whether that is good news.
 */
function Delta({ cur, prev, higherIsBetter }: { cur: number | null; prev: number | null; higherIsBetter: boolean | null }) {
  const { t, locale } = useI18n();
  const d = pctChange(cur, prev);
  if (d === null) return <span className="kpi-delta muted">{t("kpiPanel.noCompare")}</span>;
  const tone: Tone = d === 0 || higherIsBetter === null ? "neutral" : (d > 0) === higherIsBetter ? "good" : "bad";
  const pct = formatPercent(Math.abs(d), locale, 0);
  const spoken = d > 0 ? t("kpiPanel.deltaUp", { value: pct }) : d < 0 ? t("kpiPanel.deltaDown", { value: pct }) : t("kpiPanel.deltaFlat");
  return (
    <span className={`kpi-delta tone-delta-${tone}`} title={t("kpiPanel.vsPrevious")}>
      {d !== 0 && <Icon name={d > 0 ? "trendUp" : "trendDown"} size={14} />}
      <span aria-hidden="true">{d === 0 ? "±" + formatPercent(0, locale, 0) : pct}</span>
      <span className="sr-only">
        {spoken}, {t("kpiPanel.vsPrevious")}
      </span>
    </span>
  );
}

/** Tile value: a number with an optional unit (percent sign in the locale's position, or a short word). */
type Value = { text: string } | { number: string; unit?: string; unitFirst?: boolean; unitText?: boolean };

function ValueView({ v }: { v: Value }) {
  if ("text" in v) return <div className="kpi-value is-text">{v.text}</div>;
  const unit = v.unit ? <span className={`kpi-unit${v.unitText ? " kpi-unit-text" : ""}`}>{v.unit}</span> : null;
  return (
    <div className="kpi-value">
      {v.unitFirst && unit}
      <span>{v.number}</span>
      {!v.unitFirst && unit}
    </div>
  );
}

function Tile({
  icon, label, value, sub, delta, series, seriesLabel, to, tip, help,
}: {
  icon: IconName; label: string; value: Value; sub?: string; delta?: React.ReactNode; series?: (number | null)[]; seriesLabel?: string; to: string; tip?: string;
  /** Definition shown in a HelpTip next to the tile (outside the link: a button cannot sit inside a link). */
  help?: string;
}) {
  const tile = (
    <a className="kpi kpi-link" href={to} title={tip}>
      <div className="kpi-head">
        <span className="kpi-label">{label}</span>
        <span className="kpi-icon">
          <Icon name={icon} size={18} />
        </span>
      </div>
      <ValueView v={value} />
      {(delta || series) && (
        <div className="kpi-foot">
          {delta ?? <span />}
          {series && <Sparkline values={series} label={seriesLabel ?? ""} />}
        </div>
      )}
      {sub && <div className="kpi-sub">{sub}</div>}
    </a>
  );
  if (!help) return tile;
  return (
    <div className="kpi-wrap">
      {tile}
      <span className="kpi-help">
        <HelpTip topic={label}>{help}</HelpTip>
      </span>
    </div>
  );
}

const pick = <K extends keyof KpiSeriesPoint>(s: KpiSeriesPoint[], k: K) => s.map((p) => p[k] as number | null);

/**
 * Yönetici KPI paneli (ADR 0018): fleet manager and above, per site subtree, today / 7 days / 30 days with the
 * previous period. Every tile opens the matching report with the same filters. Tiles of missing modules are
 * not shown; values without data say so instead of showing a number.
 */
export function KpiPanelView({ data, route }: { data: TenantData; route: Route }) {
  const { t, locale } = useI18n();
  const { provider, tenant } = useSession();
  const site = route.query.get("site");
  const siteId = site && data.sites.some((s) => s.id === site) ? site : null;
  const p = route.query.get("period");
  const period: KpiPeriod = (KPI_PERIODS as readonly string[]).includes(p ?? "") ? (p as KpiPeriod) : "7d";
  const tenantId = tenant?.id;
  const state = useLoad<Panel>(tenantId ? () => provider.getKpiPanel(tenantId, siteId, period) : null, [provider, tenantId, siteId, period]);
  const options = useMemo(() => siteOptions(buildSiteTree(data.sites, [])), [data.sites]);
  const set = (patch: Record<string, string | undefined>) => navigate("/", { tab: "kpi", site: siteId ?? undefined, period, ...patch });
  const today = todayLocal();
  const n = period === "today" ? 1 : period === "7d" ? 7 : 30;
  const from = addDays(today, -(n - 1));
  const q = { site: siteId ?? undefined, from, to: today };
  const num = (v: number, d = 1) => v.toLocaleString(locale, { maximumFractionDigits: d });
  const pctValue = (v: number): Value => {
    const p = percentParts(v, locale);
    return { number: p.number, unit: p.sign, unitFirst: p.signFirst };
  };
  const noData = t("kpiPanel.noData");
  const k = state.data;
  const seriesLabel = t("kpiPanel.series", { days: k?.series.length ?? 0 });

  return (
    <section aria-label={t("kpiPanel.title")}>
      <div className="filters kpi-filters">
        <div className="field field-inline">
          <label htmlFor="kpi-site">{t("reports.filter.site")}</label>
          <select id="kpi-site" value={siteId ?? ""} onChange={(e) => set({ site: e.target.value || undefined })}>
            <option value="">{t("reports.filter.allSites")}</option>
            {options.map((o) => (
              <option key={o.id} value={o.id}>
                {o.label}
              </option>
            ))}
          </select>
        </div>
        <div className="segmented" role="group" aria-label={t("kpiPanel.period")}>
          {KPI_PERIODS.map((x) => (
            <button key={x} type="button" className={x === period ? "seg-on" : ""} aria-pressed={x === period} onClick={() => set({ period: x })}>
              {t(`kpiPanel.period.${x}`)}
            </button>
          ))}
        </div>
        {provider.mode === "demo" && <span className="badge tone-warn">{t("reports.sample")}</span>}
      </div>
      {state.status === "error" && (
        <p className="form-error" role="alert">
          {state.error}
        </p>
      )}
      {!k ? (
        state.status === "error" ? null : <Loading shape="cards" />
      ) : (
        <>
          <div className="kpi-grid kpi-panel" aria-busy={state.status === "loading" || undefined}>
            <Tile
              icon="chart"
              label={t("kpiPanel.utilisation")}
              value={k.utilisation.value === null ? { text: noData } : pctValue(k.utilisation.value)}
              delta={<Delta cur={k.utilisation.value} prev={k.utilisation.prev} higherIsBetter />}
              series={pick(k.series, "utilisation")}
              seriesLabel={seriesLabel}
              sub={t("kpiPanel.utilisationSub", { active: k.vehicles.active, total: k.vehicles.withDevice, basis: t(`kpiPanel.basis.${k.utilisation.basis}`) })}
              tip={t("kpiPanel.basisTip", { basis: t(`kpiPanel.basis.${k.utilisation.basis}`) })}
              help={t("help.kpi.utilisation")}
              to={href("/reports/usage", q)}
            />
            <Tile
              icon="alert"
              label={t("kpiPanel.impacts")}
              value={k.vehicles.withDevice === 0 ? { text: noData } : { number: num(k.impacts.value, 0) }}
              delta={k.vehicles.withDevice === 0 ? undefined : <Delta cur={k.impacts.value} prev={k.impacts.prev} higherIsBetter={false} />}
              series={pick(k.series, "impacts")}
              seriesLabel={seriesLabel}
              sub={k.impacts.per100h === null ? t("kpiPanel.impactsNoHours") : t("kpiPanel.impactsPer100h", { value: num(k.impacts.per100h, 2) })}
              help={t("help.kpi.impacts")}
              to={href("/reports/events", { ...q, g: "impact" })}
            />
            <Tile
              icon="check"
              label={t("kpiPanel.checklist")}
              value={!k.checklist.hasShifts ? { text: t("kpiPanel.noShifts") } : k.checklist.value === null ? { text: noData } : pctValue(k.checklist.value)}
              delta={k.checklist.hasShifts ? <Delta cur={k.checklist.value} prev={k.checklist.prev} higherIsBetter /> : undefined}
              series={k.checklist.hasShifts ? pick(k.series, "checklist") : undefined}
              seriesLabel={seriesLabel}
              sub={k.checklist.hasShifts ? t("kpiPanel.checklistSub", { missed: k.checklist.missed, used: k.checklist.used }) : t("kpiPanel.noShiftsSub")}
              help={t("help.kpi.checklist")}
              to={href("/reports/checklist", q)}
            />
            <Tile
              icon="truck"
              label={t("kpiPanel.faults")}
              value={{ number: num(k.faults.openCritical, 0) }}
              sub={t("kpiPanel.faultsSub", { count: k.faults.maintenanceVehicles })}
              to={href("/checklists/faults")}
            />
            <Tile
              icon="map"
              label={t("kpiPanel.geofence")}
              value={{ number: num(k.geofence.value, 0) }}
              delta={<Delta cur={k.geofence.value} prev={k.geofence.prev} higherIsBetter={false} />}
              series={pick(k.series, "geofence")}
              seriesLabel={seriesLabel}
              sub={t("kpiPanel.geofenceSub", { enter: k.geofence.enter, exit: k.geofence.exit, dwell: k.geofence.dwell })}
              help={t("help.kpi.geofence")}
              to={href("/reports/events", { ...q, g: "geofence" })}
            />
            {k.battery && (
              <Tile
                icon="clock"
                label={t("kpiPanel.battery")}
                value={{ number: num(k.battery.lowSoc, 0), unit: t("kpiPanel.batteryUnit"), unitText: true }}
                delta={<Delta cur={k.battery.deepDischarges} prev={k.battery.prevDeepDischarges} higherIsBetter={false} />}
                series={pick(k.series, "avgSoc")}
                seriesLabel={t("kpiPanel.seriesSoc", { days: k.series.length })}
                sub={t("kpiPanel.batterySub", { deep: k.battery.deepDischarges, avg: k.battery.avgSoc === null ? t("common.empty") : num(k.battery.avgSoc, 0) })}
                help={t("help.kpi.battery", { low: REPORT_LIMITS.lowSocPct })}
                to={href("/reports/events", { ...q, g: "battery" })}
              />
            )}
            {k.lift && (
              <Tile
                icon="box"
                label={t("kpiPanel.lift")}
                value={{ number: num(k.lift.overloads, 0) }}
                delta={<Delta cur={k.lift.overloads} prev={k.lift.prev} higherIsBetter={false} />}
                series={pick(k.series, "overloads")}
                seriesLabel={seriesLabel}
                sub={t("kpiPanel.liftSub")}
                to={href("/reports/events", { ...q, g: "overload" })}
              />
            )}
            {k.documents && (
              <Tile
                icon="card"
                label={t("kpiPanel.documents")}
                value={{ number: num(k.documents.expiring, 0) }}
                sub={t("kpiPanel.documentsSub", { expired: k.documents.expired })}
                to={href("/operators")}
              />
            )}
          </div>
          <p className="panel-footnote">
            {t("kpiPanel.footnote", { tz: k.tz })} {k.vehicles.withDevice < k.vehicles.total && t("kpiPanel.withoutDevice", { count: k.vehicles.total - k.vehicles.withDevice })}
          </p>
        </>
      )}
    </section>
  );
}
